import { useSelector } from "react-redux";
import useHttp from "../../hooks/use-http";
import { RootState } from "../../store";
import { useAppDispatch } from "../../store/hooks";
import { EventsActions } from "../../store/events-slice";
import { getEffectiveToday } from "../helpers/DateHelper";
import ConfirmModal from "../UI/ConfirmModal";
import { t } from "../../i18n";

const DeleteEventConfirm = () => {
  const dispatch = useAppDispatch();
  const { uid } = useSelector((state: RootState) => state.auth);
  const { eventToConfirmDelete: event } = useSelector(
    (state: RootState) => state.events
  );
  const { sendRequest: sendDeleteRequest } = useHttp();

  if (event === null) {
    return null;
  }

  const cancelHandler = () => {
    dispatch(EventsActions.unsetConfirmDelete());
  };

  const confirmHandler = () => {
    let body = {};
    let afterDelete: () => void;
    // A birthday always rolls to next year, whatever its repeat says: records
    // saved as birthday + "don't repeat" were promised that by the dialog below
    // and then quietly completed for good instead.
    const repeat = event.isBirthday ? "yearly" : event.repeat;
    if (repeat && repeat !== "never") {
      const oldDate = new Date(event.date);
      const todayDate = getEffectiveToday();
      while (oldDate.toISOString().slice(0, 10) <= todayDate) {
        if (repeat === "weekly") {
          oldDate.setHours(oldDate.getHours() + 24 * 7);
        } else if (repeat === "biweekly") {
          oldDate.setHours(oldDate.getHours() + 24 * 14);
        } else if (repeat === "triweekly") {
          oldDate.setHours(oldDate.getHours() + 24 * 21);
        } else if (repeat === "monthly") {
          oldDate.setMonth(oldDate.getMonth() + 1);
        } else if (repeat === "yearly") {
          oldDate.setFullYear(oldDate.getFullYear() + 1);
        }
      }

      // Advancing also clears the time, same as the mobile app: a time is
      // usually set for one particular occurrence, not for every repeat.
      const advancedDate = oldDate.toISOString().slice(0, 10);
      const advanced = { date: advancedDate, repeat, timeFrom: "", timeTill: "" };
      body = advanced;
      afterDelete = () => dispatch(EventsActions.updateEvent({ ...event, ...advanced }));
    } else {
      body = { deleted: true };
      afterDelete = () => dispatch(EventsActions.removeEvent({ id: event.id }));
    }

    sendDeleteRequest(
      {
        dbPath: "/tasks/" + uid + "/" + event.id,
        method: "PATCH",
        body: body,
        headers: {
          "Content-Type": "application/json",
        },
        hideAfterRequest: false,
        successMessage: t.eventCompleted,
      },
      afterDelete
    );

    dispatch(EventsActions.unsetConfirmDelete());
  };

  // Wording mirrors confirmCompleteHandler in the mobile app's EventCard.tsx.
  const isRepeating = !!event.repeat && event.repeat !== "never";
  let title = t.complete.title;
  let message = t.complete.message(event.title);
  let confirmLabel = t.complete.confirm;
  if (event.isBirthday) {
    title = t.complete.birthdayTitle;
    message = t.complete.birthdayMessage(event.title);
    confirmLabel = t.complete.birthdayConfirm;
  } else if (isRepeating) {
    message =
      event.timeFrom || event.timeTill
        ? t.complete.repeatWithTimeMessage(event.title)
        : t.complete.repeatMessage(event.title);
    confirmLabel = t.complete.repeatConfirm;
  }

  return (
    <ConfirmModal
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      onConfirm={confirmHandler}
      onCancel={cancelHandler}
    />
  );
};

export default DeleteEventConfirm;
